import { useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import ProduitsForm from "./ProduitsForm";
import axios from "axios";

// Définition dynamique de l'URL backend
const BACKEND_URL =
  window.location.hostname === "localhost"
    ? process.env.REACT_APP_BACKEND_URL_LOCAL
    : process.env.REACT_APP_BACKEND_URL_PROD;

export default function ProduitsModal({
  produitSelectionne,
  setProduitSelectionne,
  openFicheProduit,
  setOpenFicheProduit,
  openEdition,
  setOpenEdition,
  fetchProduits,
  openDeleteModal,
  setOpenDeleteModal,
}) {

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");

  const handleClose = () => {
    setOpenFicheProduit(false);
    setOpenEdition(false);
    setOpenDeleteModal(false);
    setOpenErrorDialog(false);
    setProduitSelectionne(null);
    setConfirmationText("");
  };

  const handleDelete = async () => {
    if (!produitSelectionne) return;
  
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${BACKEND_URL}/api/produits/${produitSelectionne._id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
  
      fetchProduits();
      handleClose();
    } catch (error) {
      console.error("❌ Erreur lors de la suppression du produit :", error);
    }
  };   

  return (
    <>
      {/* 🔹 Modal Voir Produit */}
      <Dialog open={openFicheProduit} onClose={handleClose} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/50" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-5xl bg-white rounded-lg shadow-lg p-6">
            
            {/* En-tête */}
            <div className="flex justify-between items-center border-b pb-3">
              <DialogTitle className="text-xl font-semibold">Fiche Produit</DialogTitle>
              <button onClick={handleClose} className="text-gray-500 hover:text-gray-800">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {produitSelectionne ? (
              <>
                {/* 🔹 Section : Informations Générales */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-4 border-b pb-4">
                  <p><strong>Nom :</strong> {produitSelectionne.nom_produit}</p>
                  <p><strong>Code ISIN :</strong> {produitSelectionne.code_isin}</p>
                  <p><strong>Société de Gestion :</strong> {produitSelectionne.societe_gestion}</p>
                  <p><strong>Site Web :</strong> <a href={produitSelectionne.site_web} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">{produitSelectionne.site_web}</a></p>
                  <p><strong>Type :</strong> {produitSelectionne.type}</p>
                  <p><strong>Dépositaire :</strong> {produitSelectionne.depositaire}</p>
                </div>

                {/* 🔹 Section : Détails du produit */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-4 border-b pb-4">
                  <p><strong>Objectif :</strong> {produitSelectionne.objectif}</p>
                  <p><strong>Indice de Référence :</strong> {produitSelectionne.indice_reference}</p>
                  <p><strong>Risque :</strong> {produitSelectionne.risque}/7</p>
                  <p><strong>Durée Recommandée :</strong> {produitSelectionne.duree_recommandee}</p>
                  <p><strong>Description :</strong> {produitSelectionne.description}</p>
                  <p><strong>Commentaires :</strong> {produitSelectionne.commentaires}</p>
                  <p><strong>Lien DIC :</strong> <a href={produitSelectionne.lien_dic} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">{produitSelectionne.lien_dic}</a></p>
                </div>

                {/* 🔹 Section : Stratégie d’investissement */}
                {produitSelectionne.strategie && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold border-b pb-2">Stratégie d’Investissement</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-4">
                      <p><strong>Allocation Taux :</strong> {produitSelectionne.strategie.allocation?.taux}</p>
                      <p><strong>Allocation Actions :</strong> {produitSelectionne.strategie.allocation?.actions}</p>
                      <p><strong>Allocation OPC :</strong> {produitSelectionne.strategie.allocation?.opc}</p>
                      <p><strong>Allocation Convertibles :</strong> {produitSelectionne.strategie.allocation?.convertibles}</p>
                      <p><strong>Dérivés :</strong> {produitSelectionne.strategie.derives}</p>
                      <p><strong>Sensibilité aux Taux :</strong> {produitSelectionne.strategie.sensibilite_taux}</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p>Aucune information disponible.</p>
            )}

            {/* 🔹 Boutons d'action */}
            <div className="flex justify-between mt-6">
              <button onClick={handleClose} className="px-4 py-2 bg-gray-500 text-white rounded">Fermer</button>
              <button onClick={() => { setOpenFicheProduit(false); setOpenEdition(true); }} className="px-4 py-2 bg-blue-600 text-white rounded">Modifier</button>
              <button className="px-4 py-2 bg-red-600 text-white rounded">Supprimer</button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* 🔹 Modal Modifier Produit */}
      <ProduitsForm
        open={openEdition}
        setOpen={setOpenEdition}
        produitSelectionne={produitSelectionne}
        fetchProduits={fetchProduits}
      />

      {/* 🔹 Modal de Confirmation Suppression */}
      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-black/50" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-4">
              <ExclamationTriangleIcon className="w-10 h-10 text-red-600" />
              <div>
                <DialogTitle className="text-lg font-semibold">Confirmer la suppression</DialogTitle>
                <p className="text-gray-500">Tapez <strong>"SUPPRIMER"</strong> pour confirmer :</p>
                <input
                  type="text"
                  className="mt-2 w-full border border-gray-300 rounded px-3 py-2"
                  placeholder="SUPPRIMER"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <button onClick={() => setOpenDeleteModal(false)} className="px-4 py-2 bg-gray-500 text-white rounded">Annuler</button>
              <button onClick={handleDelete} className={`px-4 py-2 rounded text-white ${confirmationText === "SUPPRIMER" ? "bg-red-600 hover:bg-red-500" : "bg-gray-400 cursor-not-allowed"}`} disabled={confirmationText !== "SUPPRIMER"}>Supprimer</button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}
